'use client';

/**
 * Closed Stage View Component
 *
 * Final state view showing completion details, deliverables, and request summary.
 * Displays:
 * - Completion success banner
 * - Request summary with metadata
 * - Link to pull request (if available)
 * - Completion timestamp
 *
 * @see Issue #241: Extract stage views into separate components
 */

import type { Request } from '@cortex/database';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Center,
  Grid,
  Heading,
  Inline,
  MetadataField,
  Stack,
  Text,
} from '@cortex/ui';
import { CheckCircle, ExternalLink, GitPullRequest } from 'lucide-react';

import type { StageViewProps } from './types';

/**
 * Closed Stage View
 * Final state with completion details
 */
export default function ClosedStageView({ request }: StageViewProps) {
  // Get PR URL from request if available
  const prUrl = (request as Request & { pr_url?: string | null }).pr_url;

  return (
    <Stack gap="lg">
      {/* Success Header */}
      <Alert variant="success" className="p-6">
        <Inline gap="sm" align="center">
          <CheckCircle className="text-success h-6 w-6" />
          <Stack gap="xs">
            <Heading level={2} className="text-lg font-semibold">
              Request Completed
            </Heading>
            <Text color="muted" size="sm">
              This request has been successfully completed and closed.
            </Text>
          </Stack>
        </Inline>
      </Alert>

      {/* Request Summary */}
      <Card className="p-6">
        <Heading level={3} className="font-semibold">
          Summary
        </Heading>

        <Stack gap="md" className="mt-4">
          <MetadataField label="Request">{request.title}</MetadataField>

          {request.description && (
            <MetadataField
              label="Description"
              valueColor="secondary"
              valueSize="sm"
            >
              {request.description}
            </MetadataField>
          )}

          <Grid columns={2} gap="md">
            <MetadataField label="Type" valueClassName="mt-1 capitalize">
              {request.type}
            </MetadataField>
            <MetadataField label="Priority" valueClassName="mt-1 capitalize">
              {request.priority}
            </MetadataField>
          </Grid>
        </Stack>
      </Card>

      {/* Links Section - if PR URL exists */}
      {prUrl && (
        <Card className="p-6">
          <Heading level={3} className="mb-4 font-semibold">
            Deliverables
          </Heading>

          <Card className="bg-surface-2">
            <CardContent className="p-4">
              <Inline justify="between" align="center">
                <Inline gap="sm" align="center">
                  <Center className="bg-success/10 h-10 w-10 rounded-lg">
                    <GitPullRequest className="text-success h-5 w-5" />
                  </Center>
                  <Stack gap="xs">
                    <Text weight="medium">Pull Request</Text>
                    <Text color="muted" size="sm">
                      Created and ready for review
                    </Text>
                  </Stack>
                </Inline>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(prUrl, '_blank')}
                >
                  <ExternalLink className="mr-2 h-4 w-4" />
                  View PR
                </Button>
              </Inline>
            </CardContent>
          </Card>
        </Card>
      )}

      {/* Completion timestamp */}
      <Text color="muted" align="center">
        Completed on{' '}
        {new Date(request.updated_at).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        })}
      </Text>
    </Stack>
  );
}
